var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import classNames from "classnames";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import PasswordInput from "@widget/forms/inputs/PasswordInput";
import FormField from "@widget/forms/inputs/FormField";
import Input from "@widget/forms/inputs/Input";
import Icon from "@icon/Icon";
import LanguageSelector from "../components/LanguageSelector";
import { getPasswordResetDetails, resetPassword } from "@core/services/auth_services";
import { $L } from "../utils/authLocalization";
import useLoadingFeedback from "@notifications/LoadingFeedback/useLoadingFeedback";
const ResetPasswordForm = (props) => {
    const { accountUrl, accountDisplayName, currentLanguage, availableLanguages, onLanguageChange, config } = props;
    const loadingFeedback = useLoadingFeedback();
    const [token, setToken] = useState("");
    const [isNew, setIsNew] = useState(false);
    const [loginID, setLoginID] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [error, setError] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [password2Error, setPassword2Error] = useState(false);
    const [success, setSuccess] = useState(false);
    const [validToken, setValidToken] = useState(false);
    const [checkingToken, setCheckingToken] = useState(true);
    useEffect(() => {
        // Get token from URL query params
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token') || '';
        const newParam = urlParams.get('new') === '1';
        setToken(tokenParam);
        setIsNew(newParam);
        // Validate token
        if (tokenParam) {
            validateToken(tokenParam);
        }
        else {
            setCheckingToken(false);
            setError($L('uxp-core.auth.reset-link-invalid', currentLanguage));
        }
    }, []);
    const validateToken = (tokenValue) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const result = yield getPasswordResetDetails(tokenValue, accountUrl);
        if (result.successful && result.data && result.data.status === 'valid') {
            // Check if security question is required and not yet validated
            if (result.data.hasSecurityQuestion && !result.data.securityQuestionValidated) {
                // Redirect to security question page
                window.location.href = `/view/auth/security-question?token=${tokenValue}`;
                return;
            }
            setValidToken(true);
            setLoginID(result.data.loginID || '');
        }
        else {
            // Set appropriate error based on status
            if (((_a = result.data) === null || _a === void 0 ? void 0 : _a.status) === 'expired') {
                setError($L('uxp-core.auth.reset-link-expired', currentLanguage));
            }
            else if (((_b = result.data) === null || _b === void 0 ? void 0 : _b.status) === 'invalid') {
                setError($L('uxp-core.auth.reset-link-invalid', currentLanguage));
            }
            else {
                setError($L('uxp-core.auth.reset-link-expired', currentLanguage));
            }
        }
        setCheckingToken(false);
    });
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        // Reset error states
        setPasswordError(false);
        setPassword2Error(false);
        setError('');
        // Validate inputs
        if (!password || !password2) {
            setError($L('uxp-core.auth.enter-passwords', currentLanguage));
            if (!password)
                setPasswordError(true);
            if (!password2)
                setPassword2Error(true);
            return;
        }
        if (password !== password2) {
            setError($L('uxp-core.auth.passwords-no-match', currentLanguage));
            setPasswordError(true);
            setPassword2Error(true);
            return;
        }
        loadingFeedback.show({ message: $L('uxp-core.auth.changing-password', currentLanguage) });
        const result = yield resetPassword({
            Hash: token,
            Password: password,
            Password2: password2
        }, accountUrl);
        loadingFeedback.hide();
        if (result.successful && !result.error) {
            setSuccess(true);
            setTimeout(() => {
                const message = $L('uxp-core.auth.password-reset-success-login', currentLanguage);
                window.location.href = '/view/auth/login?message=' + encodeURIComponent(message);
            }, 3000);
        }
        else {
            setError($L('uxp-core.auth.password-reset-failed', currentLanguage));
            setPasswordError(true);
            setPassword2Error(true);
        }
    });
    // Handle password input change - clear errors when user types
    const handlePasswordChange = (val) => {
        setPassword(val);
        if (passwordError)
            setPasswordError(false);
        if (error)
            setError('');
    };
    // Handle password2 input change - clear errors when user types
    const handlePassword2Change = (val) => {
        setPassword2(val);
        if (password2Error)
            setPassword2Error(false);
        if (error)
            setError('');
    };
    // Determine form type and settings from config
    const formType = (config === null || config === void 0 ? void 0 : config.loginForm.type) || 'island-vertical';
    const isIslandHorizontal = formType === 'island-horizontal';
    const isIslandVertical = formType === 'island-vertical';
    const isFullpage = formType === 'fullpage';
    // Get active island config
    const islandHorizontalCfg = config === null || config === void 0 ? void 0 : config.loginForm.islandHorizontal;
    const islandVerticalCfg = config === null || config === void 0 ? void 0 : config.loginForm.islandVertical;
    // Get logo from layout config
    const logo = config === null || config === void 0 ? void 0 : config.layout.logo;
    /**
     * Render the form content (used in all states)
     */
    const renderFormContent = () => {
        // Checking token state
        if (checkingToken) {
            return (_jsxs("div", { className: "uxpcore_resetpasswordform__status", children: [_jsx("h2", { className: "uxpcore_resetpasswordform__status-title", children: $L('uxp-core.auth.validating', currentLanguage) }), _jsx("p", { className: "uxpcore_resetpasswordform__status-message", children: $L('uxp-core.auth.validating-link', currentLanguage) })] }));
        }
        // Invalid token state
        if (!validToken) {
            return (_jsxs("div", { className: "uxpcore_resetpasswordform__status", children: [_jsx("h2", { className: "uxpcore_resetpasswordform__status-title", children: $L('uxp-core.auth.invalid-link', currentLanguage) }), error && (_jsx("div", { className: "uxpcore_resetpasswordform__error-message", children: error })), _jsxs("p", { className: "uxpcore_resetpasswordform__status-message", children: [$L('uxp-core.auth.link-expired-message', currentLanguage), ' ', _jsx("a", { href: "/view/auth/login", className: "uxpcore_resetpasswordform__link", children: $L('uxp-core.auth.login-screen', currentLanguage) }), ' ', $L('uxp-core.auth.and-try-again', currentLanguage)] })] }));
        }
        // Success state
        if (success) {
            return (_jsxs("div", { className: "uxpcore_resetpasswordform__status", children: [_jsx("h2", { className: "uxpcore_resetpasswordform__status-title", children: $L('uxp-core.auth.password-reset-successful', currentLanguage) }), _jsx("div", { className: "uxpcore_resetpasswordform__success-message", children: $L('uxp-core.auth.password-reset-success-message', currentLanguage) }), _jsx("p", { className: "uxpcore_resetpasswordform__status-message", children: $L('uxp-core.auth.redirecting-login', currentLanguage) })] }));
        }
        // Valid token - show form
        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_resetpasswordform__form-fields", children: [_jsx(FormField, { children: _jsx(Input, { type: "text", value: loginID, placeholder: $L('uxp-core.auth.user-id', currentLanguage), onChange: (val) => setLoginID(val), prefix: _jsx(Icon, { icon: ['far', 'user'] }), disabled: true, fullWidth: true }) }), _jsx(FormField, { children: _jsx(PasswordInput, { value: password, placeholder: $L('uxp-core.auth.new-password', currentLanguage), onChange: handlePasswordChange, prefix: _jsx(Icon, { icon: ['far', 'lock'] }), fullWidth: true, isValid: passwordError ? false : undefined }) }), _jsx(FormField, { children: _jsx(PasswordInput, { value: password2, placeholder: $L('uxp-core.auth.retype-password', currentLanguage), onChange: handlePassword2Change, prefix: _jsx(Icon, { icon: ['far', 'lock'] }), fullWidth: true, isValid: password2Error ? false : undefined }) }), error && (_jsx("div", { className: "uxpcore_resetpasswordform__error-message", children: error }))] }), _jsx("div", { className: "uxpcore_resetpasswordform__actions", children: _jsx(ButtonComponent, { title: $L('uxp-core.auth.change-password', currentLanguage), onClick: handleSubmit, disabled: !password || !password2, className: "uxpcore_resetpasswordform__submit-btn" }) })] }));
    };
    /**
     * Render island horizontal mode (single column: branding top, form below)
     */
    const renderIslandHorizontal = () => {
        var _a;
        if (!islandHorizontalCfg)
            return null;
        const title = isNew
            ? $L('uxp-core.auth.create-password', currentLanguage)
            : $L('uxp-core.auth.reset-password', currentLanguage);
        const description = $L('uxp-core.auth.reset-password-form-description', currentLanguage);
        return (_jsx("div", { className: classNames('uxpcore_resetpasswordform__island-wrapper', {
                [`uxpcore_resetpasswordform__island-wrapper--${islandHorizontalCfg.position}`]: true
            }), children: _jsxs("div", { className: classNames('uxpcore_resetpasswordform__island', 'uxpcore_resetpasswordform__island--horizontal', {
                    'uxpcore_resetpasswordform__island--glass': islandHorizontalCfg.glassEffect
                }), children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_resetpasswordform__language-selector-wrapper", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), logo && logo.url && (_jsx("div", { className: "uxpcore_resetpasswordform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_resetpasswordform__logo" }) })), validToken && !checkingToken && !success && (_jsxs(_Fragment, { children: [title && (_jsx("h1", { className: "uxpcore_resetpasswordform__title", children: title })), description && (_jsx("p", { className: "uxpcore_resetpasswordform__description", children: description }))] })), renderFormContent()] }) }));
    };
    /**
     * Render island vertical mode (two columns: branding left, form right)
     */
    const renderIslandVertical = () => {
        var _a;
        if (!islandVerticalCfg)
            return null;
        const title = isNew
            ? $L('uxp-core.auth.create-password', currentLanguage)
            : $L('uxp-core.auth.reset-password', currentLanguage);
        const description = $L('uxp-core.auth.reset-password-form-description', currentLanguage);
        return (_jsx("div", { className: classNames('uxpcore_resetpasswordform__island-wrapper', {
                [`uxpcore_resetpasswordform__island-wrapper--${islandVerticalCfg.position}`]: true
            }), children: _jsxs("div", { className: classNames('uxpcore_resetpasswordform__island', 'uxpcore_resetpasswordform__island--vertical', {
                    'uxpcore_resetpasswordform__island--glass': islandVerticalCfg.glassEffect
                }), children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_resetpasswordform__language-selector-wrapper uxpcore_resetpasswordform__language-selector-wrapper--absolute", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), _jsxs("div", { className: "uxpcore_resetpasswordform__island-left", children: [logo && logo.url && (_jsx("div", { className: "uxpcore_resetpasswordform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_resetpasswordform__logo" }) })), validToken && !checkingToken && !success && (_jsxs(_Fragment, { children: [title && (_jsx("h1", { className: "uxpcore_resetpasswordform__title", children: title })), description && (_jsx("p", { className: "uxpcore_resetpasswordform__description", children: description }))] }))] }), _jsx("div", { className: "uxpcore_resetpasswordform__island-right", children: renderFormContent() })] }) }));
    };
    /**
     * Render fullpage mode (two-column layout)
     */
    const renderFullpage = () => {
        var _a;
        const title = isNew
            ? $L('uxp-core.auth.create-password', currentLanguage)
            : $L('uxp-core.auth.reset-password', currentLanguage);
        const description = $L('uxp-core.auth.reset-password-form-description', currentLanguage);
        return (_jsxs("div", { className: "uxpcore_resetpasswordform__fullpage", children: [((_a = config === null || config === void 0 ? void 0 : config.localization) === null || _a === void 0 ? void 0 : _a.enableLanguageSelector) && availableLanguages.length > 0 && (_jsx("div", { className: "uxpcore_resetpasswordform__language-selector-wrapper uxpcore_resetpasswordform__language-selector-wrapper--absolute", children: _jsx(LanguageSelector, { languages: availableLanguages, currentLanguage: currentLanguage, onChange: onLanguageChange }) })), _jsxs("div", { className: "uxpcore_resetpasswordform__fullpage-left", children: [logo && logo.url && (_jsx("div", { className: "uxpcore_resetpasswordform__branding", children: _jsx("img", { src: logo.url, alt: logo.alt, className: "uxpcore_resetpasswordform__logo" }) })), validToken && !checkingToken && !success && (_jsxs(_Fragment, { children: [title && (_jsx("h1", { className: "uxpcore_resetpasswordform__title", children: title })), description && (_jsx("p", { className: "uxpcore_resetpasswordform__description", children: description }))] }))] }), _jsx("div", { className: "uxpcore_resetpasswordform__fullpage-right", children: _jsx("div", { className: "uxpcore_resetpasswordform__form-container", children: renderFormContent() }) })] }));
    };
    return (_jsxs("div", { className: "uxpcore_resetpasswordform__container", children: [isIslandHorizontal && renderIslandHorizontal(), isIslandVertical && renderIslandVertical(), isFullpage && renderFullpage()] }));
};
export default ResetPasswordForm;
